import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ColorSchemeProvider } from "@/components/ColorSchemeProvider";
import { AppLayout } from "./components/AppLayout";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Analytics from "./pages/Analytics";
import Planning from "./pages/Planning";
import Wellness from "./pages/Wellness";
import Recurring from "./pages/Recurring";
import Settings from "./pages/Settings";
import Accounts from "./pages/Accounts";
import Feedback from "./pages/Feedback";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";
import Onboarding from "./pages/Onboarding";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RESET_PATH } from "@/services/auth/recovery";
import { initRecoveryDetection } from "@/services/auth/recovery-init";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,  // 5 minutes - data stays fresh
      gcTime: 1000 * 60 * 30,    // 30 minutes - cache retention
      retry: 2,
      refetchOnWindowFocus: false,  // Avoid unexpected refetches
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App = () => {
  // Start watching for a password-reset link, once, for the life of the app. This used to
  // be an arrow function with an implicit return; it has a body now purely so this can run.
  // Without it nothing notices that the visitor arrived from a reset email, and they land
  // on the dashboard with the password still unset (FR-005).
  useEffect(() => initRecoveryDetection(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <ColorSchemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                {/* Public marketing surface (D-039) */}
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/privacy" element={<Privacy />} />
                {/*
                  The page a reset link lands on. Top level and OUTSIDE ProtectedRoute: it
                  has to work for somebody who cannot sign in, and the guard sends people
                  here, so putting it behind the guard would be a loop. The page gates
                  itself (FR-004).
                */}
                <Route path={RESET_PATH} element={<ResetPassword />} />
                <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><Onboarding /></ProtectedRoute>} />

                {/* Authenticated app, under /app (D-039) */}
                <Route path="/app" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
                <Route path="/app/accounts" element={<ProtectedRoute><AppLayout><Accounts /></AppLayout></ProtectedRoute>} />
                <Route path="/app/analytics" element={<ProtectedRoute><AppLayout><Analytics /></AppLayout></ProtectedRoute>} />
                <Route path="/app/planning" element={<ProtectedRoute><AppLayout><Planning /></AppLayout></ProtectedRoute>} />
                <Route path="/app/wellness" element={<ProtectedRoute><AppLayout><Wellness /></AppLayout></ProtectedRoute>} />
                <Route path="/app/recurring" element={<ProtectedRoute><AppLayout><Recurring /></AppLayout></ProtectedRoute>} />
                <Route path="/app/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
                <Route path="/app/feedback" element={<ProtectedRoute><AppLayout><Feedback /></AppLayout></ProtectedRoute>} />

                {/* Redirects for old (pre-/app) routes so existing links do not 404 */}
                <Route path="/dashboard" element={<Navigate to="/app" replace />} />
                <Route path="/accounts" element={<Navigate to="/app/accounts" replace />} />
                <Route path="/analytics" element={<Navigate to="/app/analytics" replace />} />
                <Route path="/planning" element={<Navigate to="/app/planning" replace />} />
                <Route path="/wellness" element={<Navigate to="/app/wellness" replace />} />
                <Route path="/recurring" element={<Navigate to="/app/recurring" replace />} />
                <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
                <Route path="/feedback" element={<Navigate to="/app/feedback" replace />} />
                <Route path="/trends" element={<Navigate to="/app/analytics" replace />} />
                <Route path="/insights" element={<Navigate to="/app/analytics" replace />} />
                <Route path="/reports" element={<Navigate to="/app/analytics" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </ColorSchemeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
